import React, { useState, useEffect, useCallback, useRef } from 'react';
import DOMPurify from 'dompurify';
import { useAppContext } from '../context/AppContext';
import { useNotificationContext } from '../context/NotificationContext';
import { useImpersonation } from '../context/ImpersonationContext';
import {
  getNotifications,
  markNotificationsAsRead,
  deleteNotification,
  Notification
} from '../services/apiClient';
import {
  DateRangeFilter,
  DATE_RANGE_OPTIONS,
  getDateBoundary,
  formatRelativeTime
} from '../shared/notification-types';
import '../styles/NotificationsPage.css';

const ITEMS_PER_PAGE = 20;

export const NotificationsPage: React.FC = () => {
  const { activePage } = useAppContext();
  const { refreshUnreadCount } = useNotificationContext();
  const { activeAlias } = useImpersonation();

  // Notifications data
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Track unread IDs at page open for "new" vs "old" separation
  const [initialUnreadIds, setInitialUnreadIds] = useState<Set<number>>(new Set());
  const hasMarkedAsReadRef = useRef(false);

  // Filters
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [dateRangeFilter, setDateRangeFilter] = useState<DateRangeFilter>('all');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);

  // Selection for bulk delete
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  // Deleting state
  const [deletingIds, setDeletingIds] = useState<Set<number>>(new Set());

  // Fetch notifications
  const fetchNotifications = useCallback(async (markAsReadOnFetch = false) => {
    setIsLoading(true);
    setError(null);

    try {
      const params: Record<string, string | number | boolean | undefined> = {
        skip: (currentPage - 1) * ITEMS_PER_PAGE,
        limit: ITEMS_PER_PAGE,
      };

      if (typeFilter !== 'all') {
        params.type = typeFilter;
      }

      const dateBoundary = getDateBoundary(dateRangeFilter);
      if (dateBoundary) {
        params.created_after = dateBoundary;
      }

      const response = await getNotifications(params);

      if (response.success && response.data) {
        const fetchedNotifications = response.data.notifications;
        setNotifications(fetchedNotifications);
        setTotal(response.data.total);

        // On first fetch, capture unread IDs for "new" section
        if (markAsReadOnFetch && !hasMarkedAsReadRef.current) {
          const unreadIds = fetchedNotifications
            .filter(n => !n.is_read)
            .map(n => n.id);

          setInitialUnreadIds(new Set(unreadIds));

          // Mark them as read
          if (unreadIds.length > 0) {
            // Optimistic update
            setNotifications(prev =>
              prev.map(n =>
                unreadIds.includes(n.id) ? { ...n, is_read: true } : n
              )
            );

            try {
              // API call
              await markNotificationsAsRead(unreadIds);
              hasMarkedAsReadRef.current = true;
              refreshUnreadCount();
            } catch (markError) {
              // Revert optimistic update on failure
              console.error('[NotificationsPage] Failed to mark as read:', markError);
              setNotifications(prev =>
                prev.map(n =>
                  unreadIds.includes(n.id) ? { ...n, is_read: false } : n
                )
              );
            }
          }
        }
      } else {
        setError(response.error || 'Erreur lors du chargement des notifications');
      }
    } catch (err) {
      setError('Erreur réseau');
      console.error('[NotificationsPage] Fetch error:', err);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, typeFilter, dateRangeFilter, refreshUnreadCount, activeAlias]);

  // Reset and fetch when page becomes active
  useEffect(() => {
    if (activePage === 'notifications') {
      hasMarkedAsReadRef.current = false;
      setInitialUnreadIds(new Set());
      setCurrentPage(1);
      setSelectedIds(new Set());
    }
  }, [activePage]);

  // Reset when impersonation changes
  useEffect(() => {
    setNotifications([]);
    setTotal(0);
    setCurrentPage(1);
    setSelectedIds(new Set());
    setInitialUnreadIds(new Set());
    hasMarkedAsReadRef.current = false;
  }, [activeAlias]);

  // Fetch when filters/pagination change or page becomes active
  useEffect(() => {
    if (activePage === 'notifications') {
      fetchNotifications(currentPage === 1);
    }
  }, [activePage, currentPage, typeFilter, dateRangeFilter, fetchNotifications]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
    setSelectedIds(new Set());
  }, [typeFilter, dateRangeFilter]);

  // Selection handlers
  const handleToggleSelect = (id: number) => {
    setSelectedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (selectedIds.size === notifications.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(notifications.map(n => n.id)));
    }
  };

  // Delete handlers
  const handleDeleteSingle = async (id: number) => {
    setDeletingIds(prev => new Set(prev).add(id));

    try {
      const response = await deleteNotification(id);
      if (response.success) {
        setNotifications(prev => prev.filter(n => n.id !== id));
        setSelectedIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(id);
          return newSet;
        });
        setTotal(prev => prev - 1);
        refreshUnreadCount();
      } else {
        setError(response.error || 'Erreur lors de la suppression');
      }
    } catch (err) {
      setError('Erreur réseau');
    } finally {
      setDeletingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(id);
        return newSet;
      });
    }
  };

  const handleBulkDelete = async () => {
    const idsToDelete = Array.from(selectedIds);
    if (idsToDelete.length === 0) return;

    setDeletingIds(new Set(idsToDelete));

    const successfullyDeletedIds: number[] = [];
    const failedIds: number[] = [];

    try {
      // Delete sequentially to avoid race conditions, tracking successes and failures
      for (const id of idsToDelete) {
        try {
          const response = await deleteNotification(id);
          if (response.success) {
            successfullyDeletedIds.push(id);
          } else {
            failedIds.push(id);
          }
        } catch {
          failedIds.push(id);
        }
      }

      // Update state with only successfully deleted items
      if (successfullyDeletedIds.length > 0) {
        const successfullyDeletedSet = new Set(successfullyDeletedIds);
        setNotifications(prev => prev.filter(n => !successfullyDeletedSet.has(n.id)));
        setTotal(prev => prev - successfullyDeletedIds.length);
        setSelectedIds(prev => {
          const newSet = new Set(prev);
          successfullyDeletedIds.forEach(id => newSet.delete(id));
          return newSet;
        });
        refreshUnreadCount();
      }

      // Surface errors if any deletions failed
      if (failedIds.length > 0) {
        setError(`Échec de la suppression de ${failedIds.length} notification${failedIds.length > 1 ? 's' : ''}`);
      }
    } finally {
      setDeletingIds(new Set());
    }
  };

  // Pagination
  const totalPages = Math.ceil(total / ITEMS_PER_PAGE);

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      setSelectedIds(new Set());
    }
  };

  // Separate notifications into "new" and "old"
  const newNotifications = notifications.filter(n => initialUnreadIds.has(n.id));
  const oldNotifications = notifications.filter(n => !initialUnreadIds.has(n.id));

  // Render notification content based on type
  const renderNotificationContent = (notification: Notification) => {
    switch (notification.type) {
      case 'DEFAULT':
      default:
        return (
          <div
            className="notification-text"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(notification.payload.content) }}
          />
        );
    }
  };

  // Render a single notification item
  const renderNotificationItem = (notification: Notification) => {
    const isSelected = selectedIds.has(notification.id);
    const isDeleting = deletingIds.has(notification.id);
    const isNew = initialUnreadIds.has(notification.id);

    return (
      <div
        key={notification.id}
        className={`notification-item ${isNew ? 'new' : ''} ${isDeleting ? 'deleting' : ''}`}
      >
        <input
          type="checkbox"
          className="notification-checkbox"
          checked={isSelected}
          onChange={() => handleToggleSelect(notification.id)}
          disabled={isDeleting}
        />
        <div className="notification-content">
          {renderNotificationContent(notification)}
          <div className="notification-meta">
            <span className="notification-date">
              {formatRelativeTime(notification.created_at)}
            </span>
            {notification.type !== 'DEFAULT' && (
              <span className="notification-type-badge">{notification.type}</span>
            )}
          </div>
        </div>
        <button
          className="notification-delete-btn"
          onClick={() => handleDeleteSingle(notification.id)}
          disabled={isDeleting}
          title="Supprimer"
        >
          <TrashIcon />
        </button>
      </div>
    );
  };

  return (
    <div className="notifications-page">
      <div className="notifications-container">
        {/* Header */}
        <div className="notifications-header">
          <h1 className="notifications-title">Notifications</h1>

          <div className="notifications-controls">
            {/* Filters */}
            <div className="notifications-filters">
              <select
                className="notifications-filter-select"
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
              >
                <option value="all">Tous les types</option>
                <option value="DEFAULT">Autres</option>
              </select>

              <select
                className="notifications-filter-select"
                value={dateRangeFilter}
                onChange={(e) => setDateRangeFilter(e.target.value as DateRangeFilter)}
              >
                {DATE_RANGE_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Bulk actions */}
            {selectedIds.size > 0 && (
              <div className="notifications-bulk-actions">
                <span className="bulk-selected-count">
                  {selectedIds.size} sélectionné{selectedIds.size > 1 ? 's' : ''}
                </span>
                <button
                  className="bulk-delete-btn"
                  onClick={handleBulkDelete}
                  disabled={deletingIds.size > 0}
                >
                  <TrashIcon />
                  Supprimer
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Select all checkbox */}
        {notifications.length > 0 && (
          <div className="notifications-select-all">
            <label className="select-all-label">
              <input
                type="checkbox"
                checked={selectedIds.size === notifications.length && notifications.length > 0}
                onChange={handleSelectAll}
              />
              <span>Tout sélectionner</span>
            </label>
          </div>
        )}

        {/* Content */}
        <div className="notifications-content">
          {isLoading ? (
            <div className="notifications-loading">
              <div className="loading-spinner" />
              <p>Chargement des notifications...</p>
            </div>
          ) : error ? (
            <div className="notifications-error">
              <p>{error}</p>
              <button
                className="retry-btn"
                onClick={() => fetchNotifications(false)}
              >
                Réessayer
              </button>
            </div>
          ) : notifications.length === 0 ? (
            <div className="notifications-empty">
              <BellOffIcon />
              <p>Aucune notification</p>
            </div>
          ) : (
            <>
              {/* New notifications section */}
              {newNotifications.length > 0 && (
                <div className="notifications-section">
                  <h2 className="notifications-section-title">Nouvelles</h2>
                  <div className="notifications-list">
                    {newNotifications.map(renderNotificationItem)}
                  </div>
                </div>
              )}

              {/* Old notifications section */}
              {oldNotifications.length > 0 && (
                <div className="notifications-section">
                  <h2 className="notifications-section-title">
                    {newNotifications.length > 0 ? 'Anciennes' : 'Notifications'}
                  </h2>
                  <div className="notifications-list">
                    {oldNotifications.map(renderNotificationItem)}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && !isLoading && (
          <div className="notifications-pagination">
            <button
              className="pagination-btn"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
            >
              <ChevronLeftIcon />
            </button>

            {generatePaginationNumbers(currentPage, totalPages).map((page, index) => (
              page === '...' ? (
                <span key={`ellipsis-${index}`} className="pagination-ellipsis">...</span>
              ) : (
                <button
                  key={page}
                  className={`pagination-btn ${currentPage === page ? 'active' : ''}`}
                  onClick={() => handlePageChange(page as number)}
                >
                  {page}
                </button>
              )
            ))}

            <button
              className="pagination-btn"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              <ChevronRightIcon />
            </button>
          </div>
        )}

        {/* Page info */}
        {!isLoading && total > 0 && (
          <div className="notifications-page-info">
            {`${(currentPage - 1) * ITEMS_PER_PAGE + 1}-${Math.min(currentPage * ITEMS_PER_PAGE, total)} sur ${total}`}
          </div>
        )}
      </div>
    </div>
  );
};

// Helper function to generate pagination numbers with ellipsis
function generatePaginationNumbers(current: number, total: number): (number | string)[] {
  const pages: (number | string)[] = [];

  if (total <= 7) {
    for (let i = 1; i <= total; i++) {
      pages.push(i);
    }
  } else {
    pages.push(1);

    if (current > 3) {
      pages.push('...');
    }

    const start = Math.max(2, current - 1);
    const end = Math.min(total - 1, current + 1);

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    if (current < total - 2) {
      pages.push('...');
    }

    pages.push(total);
  }

  return pages;
}

// Icons
const TrashIcon: React.FC = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    <line x1="10" y1="11" x2="10" y2="17" />
    <line x1="14" y1="11" x2="14" y2="17" />
  </svg>
);

const BellOffIcon: React.FC = () => (
  <svg
    width="48"
    height="48"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
    <path d="M18.63 13A17.89 17.89 0 0 1 18 8" />
    <path d="M6.26 6.26A5.86 5.86 0 0 0 6 8c0 7-3 9-3 9h14" />
    <path d="M18 8a6 6 0 0 0-9.33-5" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);

const ChevronLeftIcon: React.FC = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="15 18 9 12 15 6" />
  </svg>
);

const ChevronRightIcon: React.FC = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="9 18 15 12 9 6" />
  </svg>
);
